/**
 * Iter 67ca — Admin: Vendor documents dialog.
 *
 * Per-vendor modal triggered from the Vendors table. Two sections:
 *  1) Vendor-uploaded documents — admin can preview/download each, then
 *     approve or reject with an optional note.
 *  2) Admin-issued documents — admin can upload contracts/agreements for
 *     the vendor (optionally marked `requires_signature` so the vendor must
 *     upload a signed copy back, and/or `is_required_for_activation`).
 *
 * Footer surfaces a single "Documents approved" switch the admin can toggle
 * to gate vendor activation — independent from the existing `vendor_status`
 * approve/reject buttons (which control overall account approval).
 */
import { useEffect, useMemo, useRef, useState } from "react";
import api, { fileUrl, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";
import {
  FileText, Upload, Loader2, Trash2, Download, CheckCircle2, XCircle, Clock,
  FileSignature, Plus, ShieldCheck, AlertTriangle,
} from "lucide-react";

const STATUS_STYLE = {
  pending_review:     { label: "Pending review",      icon: Clock,         className: "bg-amber-50 text-amber-800 border-amber-200" },
  approved:           { label: "Approved",            icon: CheckCircle2,  className: "bg-emerald-50 text-emerald-800 border-emerald-200" },
  rejected:           { label: "Rejected",            icon: XCircle,       className: "bg-rose-50 text-rose-800 border-rose-200" },
  issued:             { label: "Issued",              icon: FileText,      className: "bg-indigo-50 text-indigo-800 border-indigo-200" },
  awaiting_signature: { label: "Awaiting signature",  icon: FileSignature, className: "bg-amber-50 text-amber-800 border-amber-200" },
  signed:             { label: "Signed",              icon: CheckCircle2,  className: "bg-emerald-50 text-emerald-800 border-emerald-200" },
};

function _resolveSrc(path) {
  if (!path) return null;
  return /^https?:\/\//i.test(path) ? path : fileUrl(path);
}

function _StatusPill({ status }) {
  const s = STATUS_STYLE[status] || { label: status, icon: FileText, className: "bg-neutral-100 text-neutral-700 border-neutral-200" };
  return (
    <Badge variant="outline" className={`inline-flex items-center gap-1 ${s.className} text-[10px] font-bold uppercase tracking-wider`}>
      <s.icon className="h-3 w-3" />
      {s.label}
    </Badge>
  );
}

async function _uploadFile(file) {
  const fd = new FormData();
  fd.append("file", file);
  const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
  return data;
}

export default function AdminVendorDocumentsDialog({ vendor, open, onOpenChange, onChanged }) {
  const [categories, setCategories] = useState([]);
  const [docs, setDocs] = useState([]);
  const [vendorInfo, setVendorInfo] = useState(null);
  const [loading, setLoading] = useState(false);
  const [reviewing, setReviewing] = useState({}); // doc_id → admin_note draft
  const [uploadDraft, setUploadDraft] = useState({ category: "contract", title: "", file: null, requires_signature: false, is_required_for_activation: false });
  const [saving, setSaving] = useState(false);
  const uploadFileRef = useRef(null);

  const load = async () => {
    if (!vendor) return;
    setLoading(true);
    try {
      const [c, r] = await Promise.all([
        api.get("/meta/vendor-document-categories"),
        api.get(`/admin/vendors/${vendor.id}/documents`),
      ]);
      setCategories(c.data || []);
      setDocs(r.data?.documents || []);
      setVendorInfo(r.data?.vendor || null);
    } catch (err) {
      toast.error(formatApiError(err) || "Could not load documents.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { if (open) load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [open, vendor?.id]);

  const _categoryLabel = (key) => categories.find((c) => c.key === key)?.label || key;
  const vendorDocs = useMemo(() => docs.filter((d) => d.owner === "vendor"), [docs]);
  const adminDocs  = useMemo(() => docs.filter((d) => d.owner === "admin"),  [docs]);

  const onReview = async (doc, decision) => {
    try {
      await api.patch(`/admin/vendor-documents/${doc.id}`, {
        decision,
        admin_note: reviewing[doc.id] || null,
      });
      toast.success(`Document ${decision}.`);
      setReviewing((r) => { const n = { ...r }; delete n[doc.id]; return n; });
      load();
      onChanged?.();
    } catch (err) { toast.error(formatApiError(err) || "Review failed."); }
  };

  const onDelete = async (doc) => {
    if (!window.confirm(`Permanently delete "${doc.title}"?`)) return;
    try {
      await api.delete(`/admin/vendor-documents/${doc.id}`);
      toast.success("Deleted.");
      load();
    } catch (err) { toast.error(formatApiError(err) || "Delete failed."); }
  };

  const onPickUploadFile = () => uploadFileRef.current?.click();
  const onUploadFileChange = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setUploadDraft((d) => ({ ...d, file: f, title: d.title || f.name.replace(/\.[^.]+$/, "") }));
  };
  const onUpload = async () => {
    if (!uploadDraft.title.trim() || !uploadDraft.file) { toast.error("Title and file required."); return; }
    setSaving(true);
    try {
      const up = await _uploadFile(uploadDraft.file);
      await api.post(`/admin/vendors/${vendor.id}/documents`, {
        category: uploadDraft.category,
        title: uploadDraft.title.trim(),
        file_path: up.path,
        original_filename: up.filename,
        content_type: up.content_type,
        requires_signature: !!uploadDraft.requires_signature,
        is_required_for_activation: !!uploadDraft.is_required_for_activation,
      });
      toast.success("Issued to vendor.");
      setUploadDraft({ category: "contract", title: "", file: null, requires_signature: false, is_required_for_activation: false });
      if (uploadFileRef.current) uploadFileRef.current.value = "";
      load();
      onChanged?.();
    } catch (err) {
      toast.error(formatApiError(err) || "Upload failed.");
    } finally {
      setSaving(false);
    }
  };

  const onToggleApproval = async (val) => {
    try {
      await api.patch(`/admin/vendors/${vendor.id}/documents-approval`, { approved: !!val });
      toast.success(val ? "Documents marked as approved." : "Documents approval revoked.");
      load();
      onChanged?.();
    } catch (err) { toast.error(formatApiError(err) || "Update failed."); }
  };

  if (!vendor) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[88vh] overflow-y-auto" data-testid="admin-vendor-docs-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ShieldCheck className="h-5 w-5 text-indigo-600" />
            Documents — {vendor.business_name || vendor.name}
          </DialogTitle>
          <DialogDescription>{vendor.email}</DialogDescription>
        </DialogHeader>

        {/* Activation gate */}
        <div className={`rounded-xl border p-3 flex items-start gap-3 ${vendorInfo?.documents_approved ? "bg-emerald-50 border-emerald-200" : "bg-amber-50 border-amber-200"}`} data-testid="admin-vendor-docs-approval">
          <div className="mt-0.5">
            {vendorInfo?.documents_approved ? <CheckCircle2 className="h-5 w-5 text-emerald-700" /> : <AlertTriangle className="h-5 w-5 text-amber-700" />}
          </div>
          <div className="flex-1">
            <div className="font-semibold text-sm">
              {vendorInfo?.documents_approved ? "Documents approved" : "Documents not yet approved"}
            </div>
            <div className="text-xs text-neutral-600 mt-0.5">
              When OFF, the vendor sees a banner reminding them to complete onboarding. Toggle on once you&apos;ve reviewed every required document.
            </div>
          </div>
          <Switch
            checked={!!vendorInfo?.documents_approved}
            onCheckedChange={onToggleApproval}
            className="data-[state=checked]:bg-emerald-600"
            data-testid="admin-vendor-docs-approval-toggle"
          />
        </div>

        {loading && <div className="py-8 text-center text-neutral-500"><Loader2 className="h-4 w-4 animate-spin inline mr-2" />Loading…</div>}

        {/* Vendor uploads */}
        {!loading && (
          <div className="rounded-xl border border-border overflow-hidden">
            <div className="px-4 py-2.5 border-b border-border bg-neutral-50 flex items-center justify-between">
              <div className="font-semibold text-sm flex items-center gap-2"><FileText className="h-4 w-4 text-neutral-500" />Vendor uploads</div>
              <Badge variant="outline" className="text-[10px]">{vendorDocs.length}</Badge>
            </div>
            {vendorDocs.length === 0 ? (
              <div className="p-6 text-center text-sm text-neutral-500">Nothing uploaded yet.</div>
            ) : (
              <div className="divide-y divide-border">
                {vendorDocs.map((d) => (
                  <div key={d.id} className="p-3 flex flex-col gap-2" data-testid={`admin-vendor-doc-${d.id}`}>
                    <div className="flex items-start justify-between gap-3 flex-wrap">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <Badge variant="outline" className="bg-amber-50 text-amber-800 border-amber-200 text-[10px] uppercase tracking-wider font-bold">
                            {_categoryLabel(d.category)}
                          </Badge>
                          <_StatusPill status={d.status} />
                        </div>
                        <div className="font-medium text-sm mt-1">{d.title}</div>
                        {d.notes && <div className="text-xs text-neutral-500 mt-0.5">Vendor note: {d.notes}</div>}
                        {d.admin_note && <div className="text-xs text-neutral-700 mt-0.5">Your last note: {d.admin_note}</div>}
                      </div>
                      <div className="flex items-center gap-1.5 flex-shrink-0">
                        <Button asChild variant="outline" size="sm" className="h-8 px-2">
                          <a href={_resolveSrc(d.file_path)} target="_blank" rel="noopener noreferrer" data-testid={`admin-vendor-doc-${d.id}-download`}>
                            <Download className="h-3.5 w-3.5 mr-1" /> Download
                          </a>
                        </Button>
                        <Button variant="ghost" size="icon" className="h-8 w-8 text-rose-600" onClick={() => onDelete(d)} data-testid={`admin-vendor-doc-${d.id}-delete`}>
                          <Trash2 className="h-3.5 w-3.5" />
                        </Button>
                      </div>
                    </div>
                    {d.status !== "approved" && (
                      <div className="flex items-end gap-2 flex-wrap pl-1">
                        <div className="flex-1 min-w-[200px]">
                          <Label className="text-[10px] uppercase tracking-wider text-neutral-500">Admin note (optional)</Label>
                          <Input
                            value={reviewing[d.id] || d.admin_note || ""}
                            onChange={(e) => setReviewing((r) => ({ ...r, [d.id]: e.target.value }))}
                            placeholder="Why are you rejecting? Or any comment."
                            className="mt-0.5 h-8 text-xs"
                            data-testid={`admin-vendor-doc-${d.id}-note`}
                          />
                        </div>
                        <Button size="sm" className="h-8 bg-emerald-600 hover:bg-emerald-700" onClick={() => onReview(d, "approved")} data-testid={`admin-vendor-doc-${d.id}-approve`}>
                          <CheckCircle2 className="h-3.5 w-3.5 mr-1" /> Approve
                        </Button>
                        <Button size="sm" variant="outline" className="h-8 border-rose-300 text-rose-700 hover:bg-rose-50" onClick={() => onReview(d, "rejected")} data-testid={`admin-vendor-doc-${d.id}-reject`}>
                          <XCircle className="h-3.5 w-3.5 mr-1" /> Reject
                        </Button>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Admin uploads */}
        {!loading && (
          <div className="rounded-xl border border-border overflow-hidden">
            <div className="px-4 py-2.5 border-b border-border bg-neutral-50 flex items-center justify-between">
              <div className="font-semibold text-sm flex items-center gap-2"><FileSignature className="h-4 w-4 text-indigo-600" />Issued to vendor</div>
              <Badge variant="outline" className="text-[10px]">{adminDocs.length}</Badge>
            </div>
            <div className="p-3 space-y-3">
              {adminDocs.length === 0 && <div className="text-sm text-neutral-500 text-center py-2">Nothing issued yet.</div>}
              {adminDocs.map((d) => (
                <div key={d.id} className="flex items-start justify-between gap-3 p-2 rounded-lg border border-border" data-testid={`admin-issued-doc-${d.id}`}>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <Badge variant="outline" className="bg-indigo-50 text-indigo-800 border-indigo-200 text-[10px] uppercase tracking-wider font-bold">
                        {_categoryLabel(d.category)}
                      </Badge>
                      <_StatusPill status={d.status} />
                      {d.is_required_for_activation && (
                        <Badge className="bg-amber-600 text-[10px]">Required</Badge>
                      )}
                    </div>
                    <div className="font-medium text-sm mt-1">{d.title}</div>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <Button asChild variant="outline" size="sm" className="h-8 px-2">
                      <a href={_resolveSrc(d.file_path)} target="_blank" rel="noopener noreferrer"><Download className="h-3.5 w-3.5 mr-1" />Doc</a>
                    </Button>
                    {d.signed_file_path && (
                      <Button asChild variant="outline" size="sm" className="h-8 px-2 border-emerald-300 text-emerald-700 hover:bg-emerald-50">
                        <a href={_resolveSrc(d.signed_file_path)} target="_blank" rel="noopener noreferrer"><Download className="h-3.5 w-3.5 mr-1" />Signed</a>
                      </Button>
                    )}
                    <Button variant="ghost" size="icon" className="h-8 w-8 text-rose-600" onClick={() => onDelete(d)}>
                      <Trash2 className="h-3.5 w-3.5" />
                    </Button>
                  </div>
                </div>
              ))}

              {/* New upload row */}
              <div className="rounded-lg border border-dashed border-border bg-neutral-50/60 p-3 space-y-2.5" data-testid="admin-issue-doc-form">
                <div className="font-semibold text-xs uppercase tracking-wider text-neutral-700">Issue new document to vendor</div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  <div>
                    <Label className="text-[10px] uppercase tracking-wider text-neutral-500">Category</Label>
                    <Select value={uploadDraft.category} onValueChange={(v) => setUploadDraft({ ...uploadDraft, category: v })}>
                      <SelectTrigger className="h-9 mt-0.5" data-testid="admin-issue-doc-category"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {categories.map((c) => <SelectItem key={c.key} value={c.key}>{c.label}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-[10px] uppercase tracking-wider text-neutral-500">Title</Label>
                    <Input
                      value={uploadDraft.title}
                      onChange={(e) => setUploadDraft({ ...uploadDraft, title: e.target.value })}
                      placeholder="e.g. Platform MSA — 2026"
                      className="mt-0.5 h-9 text-xs"
                      data-testid="admin-issue-doc-title"
                    />
                  </div>
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                  <Button variant="outline" size="sm" type="button" className="h-9" onClick={onPickUploadFile} data-testid="admin-issue-doc-pick">
                    <Upload className="h-4 w-4 mr-1" /> {uploadDraft.file ? "Change file" : "Pick file"}
                  </Button>
                  <input ref={uploadFileRef} type="file" accept="application/pdf,image/*" onChange={onUploadFileChange} className="hidden" />
                  {uploadDraft.file && <span className="text-xs text-neutral-600 truncate max-w-xs">{uploadDraft.file.name}</span>}
                </div>
                <div className="flex flex-wrap items-center gap-4">
                  <label className="flex items-center gap-2 text-xs">
                    <Switch
                      checked={uploadDraft.requires_signature}
                      onCheckedChange={(v) => setUploadDraft({ ...uploadDraft, requires_signature: !!v })}
                      data-testid="admin-issue-doc-requires-signature"
                    />
                    Requires vendor signature
                  </label>
                  <label className="flex items-center gap-2 text-xs">
                    <Switch
                      checked={uploadDraft.is_required_for_activation}
                      onCheckedChange={(v) => setUploadDraft({ ...uploadDraft, is_required_for_activation: !!v })}
                      data-testid="admin-issue-doc-required"
                    />
                    Required for activation
                  </label>
                  <Button onClick={onUpload} disabled={saving || !uploadDraft.title.trim() || !uploadDraft.file} className="ml-auto bg-indigo-600 hover:bg-indigo-700" data-testid="admin-issue-doc-save">
                    {saving ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : <Plus className="h-4 w-4 mr-1.5" />}
                    Issue
                  </Button>
                </div>
              </div>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
